"use client";

import Container from "@/components/Container";
import GradientLightText from "@/components/GradientLightText";
import Section from "@/components/Section";
import Text from "@/components/Text";
import TextBlock from "@/components/TextBlock";
import { useWindowSize } from "@/hooks/useWindowSize";
import { transformText } from "@/utils/transformText";
import { YouTubeEmbed } from "@next/third-parties/google";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper";
import { Thumbs } from "swiper/modules";
import CustomImage from "@/components/CustomImage";
import { useState } from "react";

type ImageType = {
  src: {
    desktop: string;
    tablet: string;
    mobile: string;
  };
  alt: string;
};

export type VideoType = {
  id: number;
  videoId: string;
  image: ImageType;
  title: string;
  description: string;
};

type ContentType = {
  subtitle: string;
  title: string;
  body: string;
  videos: VideoType[];
  thumbnailsTitle: string;
};

type VideoGalleryProps = {
  section: {
    type: string;
    content: ContentType;
  };
};

const VideoGallery = ({ section }: VideoGalleryProps) => {
  const { width } = useWindowSize();
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore | null>(null);
  return (
    <Section
      isPaddingTop
      isPaddingBottom
      className="bg-white darker-gold video-gallery"
    >
      <Container>
        <div className="video-gallery__holder">
          <div className="video-gallery__content">
            <TextBlock className="text-block--wider">
              <Text
                element="p"
                elementStyle="subtitle"
                className="weight-700 video-gallery__subtitle"
              >
                {section.content.subtitle}
              </Text>
              <Text element="h2" elementStyle="h1">
                {transformText(section.content.title, {
                  gradient: (chunk) => (
                    <GradientLightText
                      classNames="light-gradient--no-shadow"
                      element="span"
                    >
                      {chunk}
                    </GradientLightText>
                  ),
                })}
              </Text>
              <Text element="p" elementStyle="subtitle" className="weight-500">
                {section.content.body}
              </Text>
            </TextBlock>
            <Swiper
              allowTouchMove={false}
              thumbs={{ swiper: thumbsSwiper }}
              modules={[Thumbs]}
              className="video-gallery__swiper"
            >
              {section.content.videos.map((video) => (
                <SwiperSlide key={video.id} className="video-gallery__slide">
                  <YouTubeEmbed videoid={video.videoId} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
          <>
            {!!width ? (
              <>
                {width > 1200 ? (
                  <div className="thumbnails">
                    <div className="thumbnails__content">
                      <Text
                        element="p"
                        elementStyle="subtitle"
                        className="weight-700"
                      >
                        {section.content.thumbnailsTitle}
                      </Text>
                      <Swiper
                        onSwiper={setThumbsSwiper}
                        className="thumbnails__videos"
                        watchSlidesProgress={true}
                        modules={[Thumbs]}
                        allowTouchMove={false}
                      >
                        {section.content.videos.map((video, index) => (
                          <SwiperSlide
                            className="thumbnails__video"
                            key={video.id}
                          >
                            <CustomImage
                              alt={video.image.alt}
                              src={video.image.src.desktop}
                              srcTablet={video.image.src.tablet}
                              srcMobile={video.image.src.mobile}
                            />
                            <div className="thumbnails__video-content">
                              <Text
                                element="p"
                                elementStyle="subtitle"
                                className="weight-700"
                              >
                                {video.title}
                              </Text>
                              <Text
                                element="p"
                                elementStyle="body-secondary"
                                className="weight-500"
                              >
                                {video.description}
                              </Text>
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}
          </>
        </div>
      </Container>
    </Section>
  );
};

export default VideoGallery;
